import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Skeleton, Stack } from '@mantine/core';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { RequirePageAccess } from './auth/RequirePageAccess';
import { AppShell } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuth } from './auth/auth-context';
import type { DefaultRole } from '@synapse/shared-types';

// WHY: React.lazy for code splitting — each page loads on demand.
const Operations = lazy(() => import('./pages/Operations'));
const MetricDetail = lazy(() => import('./pages/MetricDetail'));
const Executive = lazy(() => import('./pages/Executive'));
const Board = lazy(() => import('./pages/Board'));
const Coordinator = lazy(() => import('./pages/Coordinator'));
const Governance = lazy(() => import('./pages/Governance'));
const IntegrationsHealth = lazy(() => import('./pages/IntegrationsHealth'));
const Audit = lazy(() => import('./pages/Audit'));
const Admin = lazy(() => import('./pages/Admin'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const Login = lazy(() => import('./pages/Login'));

const AssetLibraryPage = lazy(() => import('./pages/admin/AssetLibraryPage'));
const AiSpendTrackerPage = lazy(() => import('./pages/ai-spend/AiSpendTrackerPage'));
const ThreatModelListPage = lazy(() => import('./pages/threat-model/ThreatModelListPage'));
const ScopingWizardPage = lazy(() => import('./pages/threat-model/ScopingWizardPage'));
const RefinementQueuePage = lazy(() => import('./pages/threat-model/RefinementQueuePage'));
const ExecViewPage = lazy(() => import('./pages/threat-model/ExecViewPage'));
const ScenarioDetailPage = lazy(() => import('./pages/threat-model/ScenarioDetailPage'));

function PageSkeleton() {
  return (
    <Stack p="xl" gap="md">
      <Skeleton height={40} />
      <Skeleton height={200} />
      <Skeleton height={200} />
    </Stack>
  );
}

// WHY: Role hierarchy for determining the default landing page.
// Owner and coordinator land on operations (their primary working view).
// Executive and board land on their respective snapshot views.
const ROLE_LANDING: Record<DefaultRole, string> = {
  owner: '/operations',
  coordinator: '/operations',
  operations: '/operations',
  executive: '/executive',
  board: '/board',
  viewer: '/status',
};

function HomeLanding() {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) return <PageSkeleton />;

  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;

  // Pick the landing page based on the highest-priority role the user has.
  const rolePriority: DefaultRole[] = ['owner', 'coordinator', 'operations', 'executive', 'board', 'viewer'];
  for (const role of rolePriority) {
    if (user.roles.includes(role)) {
      return <Navigate to={ROLE_LANDING[role]} replace />;
    }
  }

  // Fallback — shouldn't happen if roles are assigned correctly.
  return <Navigate to="/operations" replace />;
}

// WHY: Authenticated layout wraps all pages in the AppShell navigation.
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <PageSkeleton />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <AppShell>{children}</AppShell>;
}

export function App() {
  return (
    <ErrorBoundary>
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        {/* Login page — outside the shell */}
        <Route path="/login" element={<Login />} />

        {/* Home — redirect based on role */}
        <Route path="/" element={<HomeLanding />} />

        {/* All authenticated routes wrapped in AppShell */}

        {/* Operations routes (operations+ role) */}
        <Route
          path="/operations"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['operations']}>
                <Operations />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/operations/metrics/:id"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['operations']}>
                <MetricDetail />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Executive route (executive+ role) */}
        <Route
          path="/executive"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['executive']}>
                <Executive />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Board route (board+ role) */}
        <Route
          path="/board"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['board']}>
                <Board />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Coordinator route (coordinator+ role) */}
        <Route
          path="/coordinator"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['coordinator']}>
                <Coordinator />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Governance routes (coordinator+ role) */}
        <Route
          path="/governance/risks"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['coordinator']}>
                <Governance section="risks" />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/governance/findings"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['coordinator']}>
                <Governance section="findings" />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/governance/wins-asks"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['coordinator']}>
                <Governance section="wins-asks" />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* AI Spend Tracker (dark by default — gated on /ai-spend page access) */}
        <Route
          path="/ai-spend"
          element={
            <AuthenticatedLayout>
              <RequirePageAccess path="/ai-spend">
                <AiSpendTrackerPage />
              </RequirePageAccess>
            </AuthenticatedLayout>
          }
        />

        {/* Integrations health (operations+ role) */}
        <Route
          path="/integrations"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['operations']}>
                <IntegrationsHealth />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Audit log (operations+ role) */}
        <Route
          path="/audit"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['operations']}>
                <Audit />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Status page (viewer+ — any authenticated user) */}
        <Route
          path="/status"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute>
                <StatusPage />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Admin (owner only) */}
        <Route
          path="/admin"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['owner']}>
                <Admin />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Asset Library (coordinator+ role) */}
        <Route
          path="/admin/asset-library"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['coordinator']}>
                <AssetLibraryPage />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />


        {/* Threat Models (operations+ for listing) */}
        <Route
          path="/threat-models"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['operations']}>
                <ThreatModelListPage />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/threat-models/:id/wizard"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['coordinator']}>
                <ScopingWizardPage />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/threat-models/:id/queue"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['coordinator']}>
                <RefinementQueuePage />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/threat-models/:id/executive"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['operations']}>
                <ExecViewPage />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />
        <Route
          path="/threat-models/:id/scenarios/:scenarioId"
          element={
            <AuthenticatedLayout>
              <ProtectedRoute requiredRoles={['operations']}>
                <ScenarioDetailPage />
              </ProtectedRoute>
            </AuthenticatedLayout>
          }
        />

        {/* Catch-all — redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}
